import {
  agentCapability,
  type AgentCapability,
  type HealthSample,
  type HealthSeriesResponse,
  type NodeInventory,
  type NodeType,
  type ResourcePath,
} from '@occ/contracts';
import { useCan } from '@refinedev/core';
import { m, type Variants } from 'framer-motion';
import {
  Activity,
  CheckCircle2,
  Clock,
  Cpu,
  Gauge,
  HardDrive,
  MemoryStick,
  Monitor,
  Network,
  Package,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { describeHttpError, isPendingFeature } from '../api/http';
import { FileBrowser } from '../components/FileBrowser';
import { HealthChart } from '../components/HealthChart';
import { NodeActions } from '../components/NodeActions';
import { NodeAlertsCard } from '../components/NodeAlertToggle';
import { NodeWatchesCard } from '../components/NodeWatchesCard';
import { PluginRunner } from '../components/PluginRunner';
import { ScriptRunner } from '../components/ScriptRunner';
import { ServicesPanel } from '../components/ServicesPanel';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Field,
  Loading,
  Notice,
  Select,
  StatusDot,
  Tabs,
  Toasts,
  useToasts,
} from '../components/ui';
import type { NodePanelId } from '../data/actions';
import { updateNode } from '../data/admin';
import { fetchHealthSeries, mergeLiveTail, resolutionLabel } from '../data/health';
import { fetchNodeDetail, fetchNodeInventory, type NodeDetail } from '../data/nodes';
import { useHierarchy } from '../hierarchy/HierarchyProvider';
import { bytes, percent, timeAgo, uptime } from '../lib/format';

/**
 * Ficha de nodo (Módulo 8): inventario, salud con histórico, acciones y las
 * cuatro pantallas de operación —terminal, archivos, scripts y servicios— sin
 * salir de aquí, que es el objetivo del módulo: que un incidente se resuelva en
 * una sola pantalla.
 *
 * La salud se pisa con la última muestra del canal en vivo cuando la hay: el
 * `GET /nodes/:id` da la foto al abrir, el WebSocket la mantiene. El inventario
 * no, porque cambia en horas y no en segundos.
 *
 * Las pestañas se montan **perezosamente** y a propósito: la terminal crea un
 * emulador con su propio lienzo y el navegador de archivos consulta al nodo. Que
 * las cuatro se montaran al abrir la ficha convertiría una consulta de inventario
 * en cuatro viajes al nodo.
 */

/**
 * La terminal se carga aparte.
 *
 * `xterm` con su hoja de estilos son unos 300 kB del paquete, y la mayoría de las
 * visitas a una ficha de nodo son para mirar inventario o salud. Cargarlo solo al
 * abrir la pestaña quita ese peso de la primera pantalla, que es la que decide si
 * el panel «se siente» rápido.
 */
const NodeTerminal = lazy(async () => ({
  default: (await import('../components/NodeTerminal')).NodeTerminal,
}));

const TAB_CAPABILITY: Record<NodePanelId, AgentCapability> = {
  terminal: 'terminal',
  archivos: 'files',
  scripts: 'exec',
  servicios: 'services',
};

const TAB_LABEL: Record<NodePanelId, string> = {
  terminal: 'Terminal',
  archivos: 'Archivos',
  scripts: 'Scripts',
  servicios: 'Servicios',
};

/**
 * Ventana de histórico que se pide al backend, en minutos.
 *
 * Llega hasta 30 días porque el backend ya conserva esa profundidad: el nivel de
 * cinco minutos aguanta un mes y el horario más de un año. El nivel lo elige el
 * backend por el rango —el panel no lo fuerza—, así que estos botones son la
 * pregunta y no la resolución.
 */
const HISTORY_WINDOWS = [
  { minutes: 60, label: '1 h' },
  { minutes: 60 * 6, label: '6 h' },
  { minutes: 60 * 24, label: '24 h' },
  { minutes: 60 * 24 * 7, label: '7 d' },
  { minutes: 60 * 24 * 30, label: '30 d' },
];

/**
 * Entrada del resumen al abrir la pestaña — una vez por visita, no en cada
 * refresco de salud (esas tarjetas no se remontan). Transform y opacidad
 * nada más, igual que el resto de animaciones de entrada del panel.
 */
const SUMMARY_CONTAINER: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05 } },
};
const SUMMARY_ITEM: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
};

type TabId = 'resumen' | NodePanelId;

const NODE_PANEL_IDS: NodePanelId[] = ['terminal', 'archivos', 'scripts', 'servicios'];

export function NodeDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { nodes, healthByNode, healthSeriesByNode, refresh } = useHierarchy();

  const [detail, setDetail] = useState<NodeDetail | null>(null);
  const [inventory, setInventory] = useState<NodeInventory | null>(null);
  const [inventoryLoaded, setInventoryLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // La pestaña de entrada llega por `?tab=` (el popover de acciones rápidas de
  // la topología la usa para abrir directo en "Terminal"); solo se lee al
  // montar, para no robarle la pestaña al operador si la URL cambia después.
  const [tab, setTab] = useState<TabId>(() => {
    const requested = searchParams.get('tab');
    return NODE_PANEL_IDS.includes(requested as NodePanelId) ? (requested as TabId) : 'resumen';
  });
  const [editing, setEditing] = useState(false);
  const mountedId = useRef(id);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setInventory(null);
    setInventoryLoaded(false);

    fetchNodeDetail(id)
      .then((result) => {
        if (cancelled) return;
        setDetail(result);
        setError(null);
        return fetchNodeInventory(id).then((value) => {
          if (cancelled) return;
          setInventory(value);
          setInventoryLoaded(true);
        });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : 'No se pudo cargar el nodo');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(load, [load]);

  // Al cambiar de nodo se vuelve al resumen: quedarse en «Terminal» abriría la
  // pestaña de una máquina distinta a la que se estaba mirando. No se dispara
  // en el montaje inicial, que es cuando `?tab=` decide la pestaña de entrada.
  useEffect(() => {
    if (mountedId.current === id) return;
    mountedId.current = id;
    setTab('resumen');
  }, [id]);

  const liveNode = nodes.find((node) => node.id === id);
  const status = liveNode?.status ?? detail?.node.status ?? 'unknown';
  const health = healthByNode[id] ?? detail?.health ?? null;
  const online = status === 'online';
  // Memorizado porque alimenta un `useMemo`: `?? []` crearía un array nuevo en
  // cada render y recalcularía las pestañas sin que nada hubiera cambiado.
  const capabilities = useMemo<AgentCapability[]>(
    () => detail?.capabilities ?? [],
    [detail?.capabilities],
  );

  /**
   * Capacidades que el catálogo conoce y este agente no declara. Solo se calcula
   * cuando hay lista: sin ella no se sabe qué falta, se sabe que no se pudo
   * preguntar, y enumerar las siete como «ausentes» sería mentir.
   */
  const missing = useMemo<AgentCapability[]>(
    () =>
      capabilities.length === 0
        ? []
        : agentCapability.options.filter((capability) => !capabilities.includes(capability)),
    [capabilities],
  );

  const resourcePath = useMemo(
    () => ({
      tenantId: detail?.node.tenantId ?? '',
      // El cliente sale del árbol ya cargado: `GET /nodes/:id` no lo trae.
      clientId: liveNode?.clientId,
      branchId: detail?.node.branchId,
      nodeId: id,
    }),
    [detail?.node.tenantId, detail?.node.branchId, liveNode?.clientId, id],
  );

  const tabs = useMemo(() => {
    const panels: NodePanelId[] = ['terminal', 'archivos', 'scripts', 'servicios'];
    return [
      { id: 'resumen', label: 'Resumen' },
      ...panels.map((panel) => ({
        id: panel,
        label: TAB_LABEL[panel],
        // Solo se apaga por capacidad declarada: el permiso y el estado de
        // conexión los explica cada pantalla dentro, con su motivo concreto.
        disabledReason:
          capabilities.length > 0 && !capabilities.includes(TAB_CAPABILITY[panel])
            ? `El agente de este nodo no declara la capacidad «${TAB_CAPABILITY[panel]}»`
            : undefined,
      })),
    ];
  }, [capabilities]);

  if (loading && !detail) return <Loading label="Cargando nodo…" />;
  if (error) return <Alert>{error}</Alert>;
  if (!detail) return <Alert>Nodo no encontrado.</Alert>;

  return (
    <section className="grid gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <StatusDot status={status} withLabel />
            <h1 className="text-xl font-semibold tracking-tight">{detail.node.alias}</h1>
          </div>
          <p className="text-sm opacity-60">
            {liveNode ? `${liveNode.clientName} · ${liveNode.branchName} · ` : ''}
            visto {timeAgo(detail.node.lastSeenAt)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {detail.node.agentVersion && <Badge>agente {detail.node.agentVersion}</Badge>}
          <EditNodeButton
            nodeId={detail.node.id}
            alias={detail.node.alias}
            branchId={detail.node.branchId}
            provider={detail.node.provider}
            monthlyCostCents={detail.node.monthlyCostCents}
            nodeType={detail.node.nodeType}
            resourcePath={resourcePath}
            open={editing}
            onOpenChange={setEditing}
            onSaved={() => {
              load();
              refresh();
            }}
          />
          <Link to="/" className="text-sm underline opacity-70">
            Volver a la lista
          </Link>
        </div>
      </header>

      <Tabs tabs={tabs} active={tab} onChange={(next) => setTab(next as TabId)} />

      {tab === 'resumen' && (
        <m.div variants={SUMMARY_CONTAINER} initial="hidden" animate="show" className="grid gap-4">
          <m.div variants={SUMMARY_ITEM} className="grid gap-4 lg:grid-cols-2">
            <HealthCard nodeId={id} online={online} health={health} live={healthSeriesByNode[id]} />

            <Card
              title={
                <span className="inline-flex items-center gap-2">
                  <Gauge className="size-4 opacity-70" />
                  Capacidades del agente
                </span>
              }
            >
              {capabilities.length === 0 ? (
                <p className="text-sm opacity-60">
                  {online
                    ? 'No se pudieron leer las capacidades de este nodo. Mientras tanto no se filtran las acciones por capacidad y decide la puerta.'
                    : 'Las declara el agente al conectarse, así que solo se saben con el nodo en línea. No se filtran las acciones por capacidad: decide la puerta.'}
                </p>
              ) : (
                <div className="grid gap-2">
                  <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                    {capabilities.map((capability) => (
                      <span
                        key={capability}
                        className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-2 py-1 text-xs text-emerald-700 dark:text-emerald-400"
                      >
                        <CheckCircle2 className="size-3.5 shrink-0" />
                        <span className="truncate">{capability}</span>
                      </span>
                    ))}
                    {missing.map((capability) => (
                      <span
                        key={capability}
                        className="inline-flex items-center gap-1.5 rounded-md bg-black/5 px-2 py-1 text-xs opacity-50 dark:bg-white/5"
                        title="No declarada por este agente"
                      >
                        <XCircle className="size-3.5 shrink-0" />
                        <span className="truncate">{capability}</span>
                      </span>
                    ))}
                  </div>
                  {/*
                    Lo que falta importa tanto como lo que hay: es lo que explica una
                    pestaña apagada, y sin decirlo el operador lo lee como un fallo
                    del panel en vez de como un agente que hay que actualizar.
                  */}
                  {missing.length > 0 && (
                    <p className="text-[11px] opacity-50">
                      Lo apagado no está declarado por este agente: las acciones que dependen de
                      ello quedan apagadas en este nodo.
                    </p>
                  )}
                </div>
              )}
            </Card>
          </m.div>

          <m.div variants={SUMMARY_ITEM}>
            <NodeActions
              nodeId={detail.node.id}
              alias={detail.node.alias}
              online={online}
              capabilities={capabilities}
              path={resourcePath}
              onOpenPanel={setTab}
              // El nodo ya no existe: quedarse en su ficha daría 404 en la siguiente
              // lectura. Se vuelve a la lista, que es donde el operador puede
              // comprobar que efectivamente desapareció.
              onDecommissioned={() => navigate('/nodes')}
            />
          </m.div>

          <m.div variants={SUMMARY_ITEM} className="grid gap-4 lg:grid-cols-2">
            <InventoryCard inventory={inventory} loaded={inventoryLoaded} />
            <NodeAlertsCard nodeId={id} path={resourcePath} />
          </m.div>

          <m.div variants={SUMMARY_ITEM}>
            <NodeWatchesCard
              nodeId={id}
              path={resourcePath}
              online={online}
              capabilities={capabilities}
            />
          </m.div>
        </m.div>
      )}

      {tab === 'terminal' && (
        <TerminalTab
          nodeId={id}
          alias={detail.node.alias}
          online={online}
          resourcePath={resourcePath}
        />
      )}

      {tab === 'archivos' && (
        <FileBrowser
          nodeId={id}
          alias={detail.node.alias}
          online={online}
          osFamily={inventory?.osFamily ?? detail.node.osFamily}
          resourcePath={resourcePath}
        />
      )}

      {tab === 'scripts' && (
        <ScriptTab
          nodeId={id}
          alias={detail.node.alias}
          online={online}
          osFamily={inventory?.osFamily ?? detail.node.osFamily}
          resourcePath={resourcePath}
        />
      )}

      {tab === 'servicios' && (
        <ServicesPanel
          nodeId={id}
          alias={detail.node.alias}
          online={online}
          resourcePath={resourcePath}
        />
      )}
    </section>
  );
}

/**
 * Las dos pestañas cuya acción es de un solo nombre consultan el permiso aquí y
 * no dentro del componente: así el componente recibe un motivo ya redactado y no
 * tiene que saber cómo se llama la acción en el catálogo.
 */
function TerminalTab({
  nodeId,
  alias,
  online,
  resourcePath,
}: {
  nodeId: string;
  alias: string;
  online: boolean;
  resourcePath: ResourcePath;
}) {
  const { data: permission } = useCan({
    resource: 'actions',
    action: 'terminal.open',
    params: { path: resourcePath },
  });
  return (
    <Suspense fallback={<Loading label="Cargando el emulador de terminal…" />}>
      <NodeTerminal
        nodeId={nodeId}
        alias={alias}
        online={online}
        disabledReason={permission?.can ? null : (permission?.reason ?? 'Sin permiso')}
      />
    </Suspense>
  );
}

function ScriptTab({
  nodeId,
  alias,
  online,
  osFamily,
  resourcePath,
}: {
  nodeId: string;
  alias: string;
  online: boolean;
  osFamily: NodeDetail['node']['osFamily'];
  resourcePath: ResourcePath;
}) {
  const { data: permission } = useCan({
    resource: 'actions',
    action: 'script.run',
    params: { path: resourcePath },
  });
  const reason = permission?.can ? null : (permission?.reason ?? 'Sin permiso');
  return (
    <div className="grid gap-4">
      <PluginRunner
        nodeId={nodeId}
        alias={alias}
        online={online}
        osFamily={osFamily}
        clientId={resourcePath.clientId}
        disabledReason={reason}
      />
      <ScriptRunner
        nodeId={nodeId}
        alias={alias}
        online={online}
        osFamily={osFamily}
        disabledReason={reason}
      />
    </div>
  );
}

/**
 * Salud con histórico (Módulo 7).
 *
 * La serie viene de `GET /nodes/:id/health`, que la persiste de verdad: sobrevive
 * a recargas y llega a treinta días. El canal en vivo sigue teniendo un papel, pero
 * solo uno: el **tramo más reciente**. El backend escribe en lote cada pocos
 * segundos, así que lo último que este navegador ha visto por el WebSocket es más
 * nuevo que lo último que la base tiene, y sin empalmarlo la gráfica de un nodo que
 * se está mirando ahora mismo terminaría en el pasado y parecería congelada.
 *
 * Se dice siempre **qué grano** se está viendo. Una gráfica de treinta días con
 * puntos horarios y otra de una hora con puntos de 30 s no se leen igual: en la
 * primera un pico de un minuto queda aplanado por la media, y presentarlas sin
 * distinguirlas hace concluir que no pasó nada.
 */
function HealthCard({
  nodeId,
  online,
  health,
  live,
}: {
  nodeId: string;
  online: boolean;
  health: HealthSample | null;
  live: HealthSample[] | undefined;
}) {
  const [minutes, setMinutes] = useState(HISTORY_WINDOWS[1]?.minutes ?? 360);
  const [series, setSeries] = useState<HealthSeriesResponse | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSeries(null);
    fetchHealthSeries(nodeId, minutes)
      .then((response) => {
        if (cancelled) return;
        setSeries(response);
        setUnavailable(false);
        setFailure(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Un 404/501 significa que el módulo de la serie no está cableado en este
        // despliegue: no es un fallo que merezca pantalla roja, se cae a la serie
        // en vivo y se explica una vez. Cualquier otro error sí se nombra, porque
        // callarlo dejaría una gráfica corta sin motivo aparente.
        const pending = isPendingFeature(error);
        setUnavailable(pending);
        setFailure(pending ? null : describeHttpError(error));
        setSeries(null);
      });
    return () => {
      cancelled = true;
    };
  }, [nodeId, minutes]);

  const resolution = series?.resolution ?? 'raw';
  const samples = useMemo(
    () => mergeLiveTail(series?.samples ?? [], live, resolution),
    [series?.samples, live, resolution],
  );
  const fromLive = !series || series.samples.length === 0;

  // Bytes y no solo el porcentaje: «84 %» no dice si sobra memoria o si faltan
  // 300 MB, y el total es del inventario, que la serie trae en el envoltorio.
  const memoryHint =
    series?.memoryTotalBytes && health?.memoryUsedPercent !== undefined
      ? `${bytes(Math.round((series.memoryTotalBytes * health.memoryUsedPercent) / 100))} de ${bytes(series.memoryTotalBytes)}`
      : undefined;

  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <Activity className="size-4 opacity-70" />
          Salud
        </span>
      }
      actions={
        <div className="flex flex-wrap items-center gap-1">
          {HISTORY_WINDOWS.map((window) => (
            <button
              key={window.minutes}
              type="button"
              onClick={() => setMinutes(window.minutes)}
              className={`rounded px-1.5 py-0.5 text-[11px] transition ${
                minutes === window.minutes
                  ? 'bg-[var(--occ-brand)] text-white'
                  : 'border border-black/10 dark:border-white/15 opacity-70 hover:opacity-100'
              }`}
            >
              {window.label}
            </button>
          ))}
        </div>
      }
    >
      <div className="grid gap-3">
        {!health ? (
          <p className="text-sm opacity-60">
            Sin muestras. {online ? 'Esperando el próximo latido.' : 'El nodo no está conectado.'}
          </p>
        ) : (
          <>
            <div className="grid gap-3">
              <Meter icon={Cpu} label="CPU" value={health.cpuPercent} />
              <Meter
                icon={MemoryStick}
                label="Memoria"
                value={health.memoryUsedPercent}
                hint={memoryHint}
              />
              <Meter icon={HardDrive} label="Disco" value={health.diskUsedPercent} />
              <dl className="grid grid-cols-2 gap-1 text-sm">
                <dt className="flex items-center gap-1.5 opacity-60">
                  <Clock className="size-3.5" />
                  Encendido
                </dt>
                <dd>{uptime(health.uptimeSeconds)}</dd>
                <dt className="opacity-60">Muestra</dt>
                <dd>{timeAgo(health.sampledAt)}</dd>
              </dl>
            </div>

            {samples.length > 1 && <HealthChart samples={samples} />}

            <p className="text-[11px] opacity-50">
              {fromLive
                ? 'Serie del canal en vivo, solo desde que abriste la pestaña.'
                : `Histórico ${resolutionLabel(resolution)}, empalmado con el canal en vivo al final.`}
            </p>
          </>
        )}

        {series?.degraded && (
          <Notice tone="pending" title={`Se sirvió el grano ${resolutionLabel(resolution)}`}>
            El detalle fino de esta ventana ya venció por retención, así que el backend ha servido
            el nivel que sí la cubre. Las medias siguen siendo exactas, pero un pico de un minuto
            queda aplanado: para ver picos hay que pedir una ventana más corta.
          </Notice>
        )}

        {failure && <Alert>{failure}</Alert>}

        {fromLive && unavailable && (
          <Notice tone="pending" title="El histórico no está disponible en este despliegue">
            <code>GET /nodes/:id/health</code> no responde, así que la gráfica es la serie que este
            navegador ha visto por el canal en vivo desde que abriste la pestaña: se pierde al
            recargar. La tabla y la ruta existen; falta registrar <code>HealthSeriesModule</code> en
            el backend.
          </Notice>
        )}
      </div>
    </Card>
  );
}

function InventoryCard({
  inventory,
  loaded,
}: {
  inventory: NodeInventory | null;
  loaded: boolean;
}) {
  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <Monitor className="size-4 opacity-70" />
          Inventario
        </span>
      }
    >
      {!loaded ? (
        <Loading label="Cargando inventario…" />
      ) : !inventory ? (
        <p className="text-sm opacity-60">
          El nodo aún no ha reportado inventario. Lo manda el agente en el primer latido.
        </p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <dl className="grid grid-cols-[9rem_1fr] gap-y-1 text-sm">
            <dt className="opacity-60">Host</dt>
            <dd>{inventory.hostname}</dd>
            <dt className="opacity-60">Sistema</dt>
            <dd>{inventory.osVersion}</dd>
            <dt className="opacity-60">Arquitectura</dt>
            <dd>{inventory.architecture}</dd>
            <dt className="flex items-center gap-1.5 opacity-60">
              <Cpu className="size-3.5" />
              CPU
            </dt>
            <dd>
              {inventory.cpuModel ?? '—'}
              {inventory.cpuCores ? ` (${inventory.cpuCores} núcleos)` : ''}
            </dd>
            <dt className="flex items-center gap-1.5 opacity-60">
              <MemoryStick className="size-3.5" />
              Memoria
            </dt>
            <dd>{bytes(inventory.memoryTotalBytes)}</dd>
            <dt className="flex items-center gap-1.5 opacity-60">
              <Network className="size-3.5" />
              IPs
            </dt>
            <dd>{inventory.localIps.join(', ') || '—'}</dd>
            <dt className="opacity-60">Recogido</dt>
            <dd>{timeAgo(inventory.collectedAt)}</dd>
          </dl>

          <div className="grid gap-3">
            <div>
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide opacity-50">
                <HardDrive className="size-3.5" />
                Discos
              </div>
              <ul className="text-sm">
                {inventory.disks.map((disk) => (
                  <li key={disk.mountpoint} className="flex justify-between gap-3">
                    <span>{disk.mountpoint}</span>
                    <span className="opacity-70">
                      {bytes(disk.totalBytes - disk.freeBytes)} de {bytes(disk.totalBytes)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <SoftwareList software={inventory.installedSoftware} />
          </div>
        </div>
      )}
    </Card>
  );
}

/** A partir de cuántos programas aparece el buscador. */
const SOFTWARE_FILTER_FROM = 8;

/**
 * Los programas instalados, con tope de alto.
 *
 * Una caja de cliente tiene cientos, y como lista abierta empujaba todo lo demás
 * de la ficha varias pantallas hacia abajo. Ahora ocupa una altura fija con su
 * propio desplazamiento, y con muchos programas trae un buscador: quien abre esto
 * casi siempre busca uno concreto, no los lee todos.
 */
function SoftwareList({ software }: { software: NodeInventory['installedSoftware'] }) {
  const [filter, setFilter] = useState('');
  const needle = filter.trim().toLowerCase();
  const visible = needle
    ? software.filter((item) => item.name.toLowerCase().includes(needle))
    : software;

  return (
    <div className="grid gap-1.5">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide opacity-50">
        <Package className="size-3.5" />
        Software
        <span className="normal-case tracking-normal">({software.length})</span>
      </div>
      {software.length > SOFTWARE_FILTER_FROM && (
        <input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Buscar programa…"
          aria-label="Buscar programa instalado"
          className="w-full rounded bg-accent/40 px-2 py-1 text-xs shadow-sm outline-none focus:shadow-[0_0_0_2px_var(--ring)]"
        />
      )}
      {software.length === 0 ? (
        <p className="text-sm opacity-60">El agente no reportó programas instalados.</p>
      ) : visible.length === 0 ? (
        <p className="text-sm opacity-60">Ningún programa coincide.</p>
      ) : (
        <ul className="max-h-48 overflow-y-auto pr-1 text-sm">
          {visible.map((item) => (
            <li key={item.name} className="flex justify-between gap-3">
              <span className="min-w-0 truncate" title={item.name}>
                {item.name}
              </span>
              <span className="shrink-0 opacity-70">{item.version ?? '—'}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Renombrar y trasladar el nodo (RF-17).
 *
 * El alias es la forma en que un técnico se refiere a la máquina por teléfono
 * («la caja 1 de Frutal Centro»), así que editarlo tiene que estar donde se está
 * mirando el nodo y no enterrado en una pantalla de administración.
 */
function EditNodeButton({
  nodeId,
  alias,
  branchId,
  provider,
  monthlyCostCents,
  nodeType,
  resourcePath,
  open,
  onOpenChange,
  onSaved,
}: {
  nodeId: string;
  alias: string;
  branchId: string;
  provider: string | null;
  monthlyCostCents: number | null;
  nodeType: NodeType | null;
  resourcePath: ResourcePath;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const { tree } = useHierarchy();
  const { data: permission } = useCan({
    resource: 'nodes',
    action: 'edit',
    params: { path: resourcePath },
  });

  const [draftAlias, setDraftAlias] = useState(alias);
  const [draftBranch, setDraftBranch] = useState(branchId);
  const [draftProvider, setDraftProvider] = useState(provider ?? '');
  // Texto libre y no número controlado: así el campo puede estar vacío
  // mientras se escribe sin que React lo fuerce de vuelta a `0`.
  const [draftMonthlyCost, setDraftMonthlyCost] = useState(
    monthlyCostCents === null ? '' : (monthlyCostCents / 100).toString(),
  );
  const [draftNodeType, setDraftNodeType] = useState(nodeType ?? '');
  const [busy, setBusy] = useState(false);
  const toasts = useToasts();

  useEffect(() => {
    if (open) {
      setDraftAlias(alias);
      setDraftBranch(branchId);
      setDraftProvider(provider ?? '');
      setDraftMonthlyCost(monthlyCostCents === null ? '' : (monthlyCostCents / 100).toString());
      setDraftNodeType(nodeType ?? '');
    }
  }, [open, alias, branchId, provider, monthlyCostCents, nodeType]);

  const branches = (tree?.clients ?? []).flatMap((client) =>
    client.branches.map((branch) => ({
      id: branch.id,
      label: `${client.name} · ${branch.name}`,
    })),
  );

  const save = async () => {
    setBusy(true);
    try {
      const trimmedProvider = draftProvider.trim();
      const nextProvider = trimmedProvider || null;
      const nextMonthlyCostCents =
        draftMonthlyCost.trim() === '' ? null : Math.round(Number(draftMonthlyCost) * 100);
      const nextNodeType = draftNodeType || null;

      await updateNode(nodeId, {
        alias: draftAlias !== alias ? draftAlias : undefined,
        branchId: draftBranch !== branchId ? draftBranch : undefined,
        provider: nextProvider !== provider ? nextProvider : undefined,
        monthlyCostCents:
          nextMonthlyCostCents !== monthlyCostCents ? nextMonthlyCostCents : undefined,
        nodeType: nextNodeType !== nodeType ? (nextNodeType as NodeType | null) : undefined,
      });
      toasts.ok('Nodo actualizado.');
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toasts.fail(error instanceof Error ? error.message : 'No se pudo actualizar el nodo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Toasts toasts={toasts.toasts} />
      <Button
        variant="secondary"
        disabled={!permission?.can}
        title={permission?.can ? 'Renombrar o mover el nodo' : permission?.reason}
        onClick={() => onOpenChange(true)}
      >
        Editar
      </Button>

      <ConfirmDialog
        open={open}
        title="Editar el nodo"
        confirmLabel="Guardar"
        danger={false}
        busy={busy}
        onCancel={() => onOpenChange(false)}
        onConfirm={() => void save()}
      >
        <Field
          label="Alias"
          value={draftAlias}
          hint="Único por sucursal. Letras, números, punto, guion y guion bajo."
          onChange={(event) => setDraftAlias(event.target.value)}
        />
        <Select
          label="Sucursal"
          value={draftBranch}
          onChange={(event) => setDraftBranch(event.target.value)}
        >
          {branches.map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.label}
            </option>
          ))}
        </Select>
        <Notice tone="pending">
          Mover un nodo cambia quién puede verlo: los permisos con alcance de cliente o de sucursal
          se resuelven por esta cadena.
        </Notice>
        <Select
          label="Tipo de nodo"
          value={draftNodeType}
          onChange={(event) => setDraftNodeType(event.target.value as NodeType | '')}
        >
          <option value="">Sin especificar</option>
          <option value="vps">VPS</option>
          <option value="fisico">Equipo físico</option>
        </Select>
        <Field
          label="Provider"
          value={draftProvider}
          hint="Solo aplica a VPS. Vacío si es un equipo físico del cliente."
          onChange={(event) => setDraftProvider(event.target.value)}
        />
        <Field
          label="Costo mensual (USD)"
          type="number"
          min="0"
          step="0.01"
          value={draftMonthlyCost}
          hint="Vacío si no tiene un costo fijo que llevar."
          onChange={(event) => setDraftMonthlyCost(event.target.value)}
        />
      </ConfirmDialog>
    </>
  );
}

/** Por encima de esto, el medidor se pinta de aviso o de riesgo — misma lógica en las tres métricas. */
const METER_WARN_AT = 75;
const METER_CRITICAL_AT = 90;

function Meter({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: number | undefined;
  /** Lo mismo en unidades absolutas, cuando se sabe: «13,4 GiB de 16». */
  hint?: string;
}) {
  const width = Math.min(100, Math.max(0, value ?? 0));
  const barClass =
    width >= METER_CRITICAL_AT
      ? 'bg-destructive'
      : width >= METER_WARN_AT
        ? 'bg-amber-500'
        : 'bg-[var(--occ-brand)]';
  return (
    <div className="grid gap-1">
      <div className="flex justify-between text-sm">
        <span className="inline-flex items-center gap-1.5 opacity-70">
          <Icon className="size-3.5" />
          {label}
        </span>
        <span>
          {hint && <span className="mr-2 text-xs opacity-50">{hint}</span>}
          {percent(value)}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded bg-black/10 dark:bg-white/10">
        <m.div
          className={`h-full rounded ${barClass}`}
          initial={{ width: 0 }}
          animate={{ width: `${width}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
}
